/**
 * Banner Manager (DWA Internal → Web).
 *
 * Author + schedule banners that the WordPress [lupa_banner] shortcode renders
 * on client community sites (replaces per-site Opt-In Monster). Structured
 * fields → Lupa renders the HTML; a schedule (start/end) + enable toggle
 * control when it shows. Targeting: all sites / a community group / one site.
 *
 * Schedule times are entered in the editor's local timezone and stored as
 * absolute UTC, so a banner shows/hides at the same real moment everywhere.
 * (Per-community local-time scheduling is a planned future upgrade.)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchBanners, createBanner, updateBanner, deleteBanner,
  BANNER_CLIENTS,
  type Banner, type BannerInput, type BannerListResponse, type BrandPreset,
  type BannerTargetType, type BannerStatus, type BackendClientId,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface BannerManagerProps {
  theme: ScorecardTheme;
}

const DEFAULTS = {
  bg_color: '#1a1a1a', text_color: '#ffffff', button_color: '#fec35c',
  // Only used to seed the picker when hover is switched on — null means the
  // button keeps one flat color, matching pre-hover-option banners.
  button_hover_color: '#e5a93f',
  // Was hardcoded in both the renderer and this preview.
  button_text_color: '#111111',
  // Seeds the extra stops when a gradient / 3-colour mode is switched on.
  gradient_to: '#3a3a3a',
  gradient_mid: '#ffffff',
};

/** Mirrors banner_service._background: gradient only when BOTH stops are set. */
function bannerBackground(b: Pick<BannerInput, 'bg_color' | 'bg_gradient_from' | 'bg_gradient_mid' | 'bg_gradient_to' | 'bg_gradient_angle'>): string {
  const flat = b.bg_color || DEFAULTS.bg_color;
  if (!b.bg_gradient_from || !b.bg_gradient_to) return flat;
  const angle = ((b.bg_gradient_angle ?? 180) % 360 + 360) % 360;
  const stops = b.bg_gradient_mid
    ? `${b.bg_gradient_from}, ${b.bg_gradient_mid}, ${b.bg_gradient_to}`
    : `${b.bg_gradient_from}, ${b.bg_gradient_to}`;
  return `linear-gradient(${angle}deg, ${stops})`;
}

const GRADIENT_KEYS = [
  'bg_gradient_from', 'bg_gradient_mid', 'bg_gradient_to', 'bg_gradient_angle',
] as const;

const COLOR_KEYS = [
  'bg_color', 'text_color', 'button_color', 'button_hover_color',
  'button_text_color', 'button_text_hover_color',
  ...GRADIENT_KEYS,
] as const;

type ColorSet = Pick<BannerInput, typeof COLOR_KEYS[number]>;

function emptyBanner(): BannerInput {
  return {
    name: '', target_type: 'all', target_group: null, target_site_id: null,
    headline: '', message: '', button_label: '', button_url: '',
    bg_color: DEFAULTS.bg_color, text_color: DEFAULTS.text_color, button_color: DEFAULTS.button_color,
    button_hover_color: null, button_text_color: null, button_text_hover_color: null,
    bg_gradient_from: null, bg_gradient_mid: null, bg_gradient_to: null,
    bg_gradient_angle: null, button_new_tab: false,
    dismissible: false, start_at: null, end_at: null, is_enabled: true,
  };
}

// ─── Date <-> datetime-local helpers ───────────────────────────────────────

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function browserTz(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local'; } catch { return 'local'; }
}

function fmtWindow(b: Banner): string {
  const f = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : null);
  const s = f(b.start_at); const e = f(b.end_at);
  if (!s && !e) return 'Always on (while enabled)';
  if (s && !e) return `From ${s}`;
  if (!s && e) return `Until ${e}`;
  return `${s} → ${e}`;
}

// ─── Status badge ───────────────────────────────────────────────────────────

const STATUS_COLOR: Record<BannerStatus, string> = {
  active: '#15803d', scheduled: '#0ea5e9', expired: '#94a3b8', disabled: '#94a3b8',
};

const StatusBadge: React.FC<{ status: BannerStatus }> = ({ status }) => (
  <span style={{
    fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    padding: '0.12rem 0.5rem', borderRadius: 3, color: '#fff', background: STATUS_COLOR[status],
  }}>{status}</span>
);

// ─── Live preview (built from fields — no raw HTML injection) ───────────────

const BannerPreview: React.FC<{ b: BannerInput }> = ({ b }) => {
  // Mirrors the renderer's hover swap so the picker can be judged by hovering
  // the preview button rather than by publishing.
  const [hovering, setHovering] = useState(false);
  const btnBg = (hovering && b.button_hover_color) || b.button_color || DEFAULTS.button_color;
  const btnFg = (hovering && b.button_text_hover_color)
    || b.button_text_color || DEFAULTS.button_text_color;
  return (
    <div style={{
      position: 'relative', background: bannerBackground(b), color: b.text_color || DEFAULTS.text_color,
      padding: '0.7rem 2.2rem', textAlign: 'center', fontSize: '0.95rem', borderRadius: 4,
    }}>
      {b.headline && <span style={{ fontWeight: 700, marginRight: '0.5rem' }}>{b.headline}</span>}
      {b.message && <span>{b.message}</span>}
      {b.button_label && b.button_url && (
        <span
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          style={{
            display: 'inline-block', marginLeft: '0.75rem', padding: '0.25rem 0.7rem', borderRadius: 4,
            background: btnBg, color: btnFg, fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.5,
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
        >{b.button_label}</span>
      )}
      {b.dismissible && <span style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.7 }}>×</span>}
    </div>
  );
};

// ─── Editor ─────────────────────────────────────────────────────────────────

const Editor: React.FC<{
  theme: ScorecardTheme;
  initial: BannerInput;
  targets: BannerListResponse['targets'];
  presets: Record<string, BrandPreset>;
  /** Only a new banner auto-fills; editing an existing one must not restyle it. */
  isNew: boolean;
  saving: boolean;
  onSave: (b: BannerInput) => void;
  onCancel: () => void;
}> = ({ theme, initial, targets, presets, isNew, saving, onSave, onCancel }) => {
  const [b, setB] = useState<BannerInput>(initial);
  // Tracks whether the author has changed any color, so auto-fill never
  // overwrites deliberate choices.
  const colorsTouched = useRef(false);
  const set = <K extends keyof BannerInput>(k: K, v: BannerInput[K]) => {
    if ((COLOR_KEYS as readonly string[]).includes(k as string)) colorsTouched.current = true;
    setB((p) => ({ ...p, [k]: v }));
  };

  // Which brand does the current target resolve to? A site maps to exactly one
  // template. A group only resolves when every site in it shares one template —
  // e.g. PLC's 'Individual' group spans two, so it stays ambiguous.
  const targetTemplate: string | null = useMemo(() => {
    if (b.target_type === 'site' && b.target_site_id) {
      return targets.sites.find((s) => s.id === b.target_site_id)?.community_template ?? null;
    }
    if (b.target_type === 'group' && b.target_group) {
      const tpls = new Set(
        targets.sites
          .filter((s) => s.community_group === b.target_group)
          .map((s) => s.community_template)
          .filter((x): x is string => !!x),
      );
      return tpls.size === 1 ? [...tpls][0] : null;
    }
    return null;
  }, [b.target_type, b.target_site_id, b.target_group, targets.sites]);

  const preset = targetTemplate ? presets[targetTemplate] : undefined;

  const label: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 600, color: theme.textSecondary, display: 'block', marginBottom: '0.25rem' };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', padding: '0.4rem 0.55rem',
    borderRadius: 4, border: `1px solid ${theme.cardBorder}`, background: theme.cardBg, color: theme.textPrimary, fontFamily: 'inherit',
  };
  const field = (l: string, node: React.ReactNode) => (<div><label style={label}>{l}</label>{node}</div>);

  const valid = b.name.trim() !== '' &&
    (b.target_type !== 'group' || !!b.target_group) &&
    (b.target_type !== 'site' || !!b.target_site_id);

  // Both stops required, matching the renderer — a half-set gradient falls back
  // to the flat colour rather than rendering something broken.
  const gradientOn = !!(b.bg_gradient_from && b.bg_gradient_to);

  // Colors only — leaves content, targeting, and schedule alone. Hover resets
  // to null (off), which is the unstyled default rather than a color.
  // Reset target: the brand's colors when the target resolves to one, else the
  // global defaults. That's what "reset" means to whoever authored the brand.
  const resetTarget: ColorSet = preset
    ? {
        bg_color: preset.bg_color ?? DEFAULTS.bg_color,
        text_color: preset.text_color ?? DEFAULTS.text_color,
        button_color: preset.button_color ?? DEFAULTS.button_color,
        button_hover_color: preset.button_hover_color ?? null,
        button_text_color: preset.button_text_color ?? null,
        button_text_hover_color: preset.button_text_hover_color ?? null,
        bg_gradient_from: preset.bg_gradient_from ?? null,
        bg_gradient_mid: preset.bg_gradient_mid ?? null,
        bg_gradient_to: preset.bg_gradient_to ?? null,
        bg_gradient_angle: preset.bg_gradient_angle ?? null,
      }
    : {
        bg_color: DEFAULTS.bg_color,
        text_color: DEFAULTS.text_color,
        button_color: DEFAULTS.button_color,
        button_hover_color: null,
        button_text_color: null,
        button_text_hover_color: null,
        bg_gradient_from: null,
        bg_gradient_mid: null,
        bg_gradient_to: null,
        bg_gradient_angle: null,
      };

  const colorsMatchTarget = COLOR_KEYS.every((k) => (b[k] ?? null) === (resetTarget[k] ?? null));

  const applyColors = (set_: ColorSet) => {
    colorsTouched.current = true;
    setB((p) => ({ ...p, ...set_ }));
  };

  const resetColors = () => applyColors(resetTarget);

  // Auto-fill brand colors when the target resolves to a brand — but only on a
  // new banner, and only while the author hasn't touched a color themselves.
  // Editing an existing banner never auto-restyles it; use the button instead.
  useEffect(() => {
    if (!isNew || !preset || colorsTouched.current) return;
    setB((p) => ({
      ...p,
      bg_color: preset.bg_color ?? DEFAULTS.bg_color,
      text_color: preset.text_color ?? DEFAULTS.text_color,
      button_color: preset.button_color ?? DEFAULTS.button_color,
      button_hover_color: preset.button_hover_color ?? null,
      button_text_color: preset.button_text_color ?? null,
      button_text_hover_color: preset.button_text_hover_color ?? null,
      bg_gradient_from: preset.bg_gradient_from ?? null,
      bg_gradient_mid: preset.bg_gradient_mid ?? null,
      bg_gradient_to: preset.bg_gradient_to ?? null,
      bg_gradient_angle: preset.bg_gradient_angle ?? null,
    }));
    // Intentionally not depending on `preset` identity beyond the template key —
    // the object is rebuilt on every fetch.
  }, [isNew, targetTemplate]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow,
      borderRadius: 4, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
    }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: theme.textPrimary }}>Preview</div>
      <BannerPreview b={b} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field('Name (internal)', <input style={input} value={b.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Fall Festival" />)}
        {field('Target', (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <select style={{ ...input, flex: '0 0 40%' }} value={b.target_type}
              onChange={(e) => set('target_type', e.target.value as BannerTargetType)}>
              <option value="all">All sites</option>
              <option value="group">Community group</option>
              <option value="site">One site</option>
            </select>
            {b.target_type === 'group' && (
              <select style={input} value={b.target_group || ''} onChange={(e) => set('target_group', e.target.value || null)}>
                <option value="">Select group…</option>
                {targets.groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            )}
            {b.target_type === 'site' && (
              <select style={input} value={b.target_site_id ?? ''} onChange={(e) => set('target_site_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">Select site…</option>
                {targets.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>

      {field('Headline', <input style={input} value={b.headline || ''} onChange={(e) => set('headline', e.target.value)} />)}
      {field('Message', <input style={input} value={b.message || ''} onChange={(e) => set('message', e.target.value)} />)}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field('Button label', <input style={input} value={b.button_label || ''} onChange={(e) => set('button_label', e.target.value)} placeholder="Optional" />)}
        {field('Button URL', <input style={input} value={b.button_url || ''} onChange={(e) => set('button_url', e.target.value)} placeholder="https://…" />)}
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {field('Background', <input type="color" value={b.bg_color || DEFAULTS.bg_color} onChange={(e) => set('bg_color', e.target.value)} style={{ width: 44, height: 32, border: 'none', background: 'none', cursor: 'pointer' }} />)}
        {field('Gradient', (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={gradientOn}
              title="Use a gradient background instead of a flat colour"
              onChange={(e) => {
                if (e.target.checked) {
                  setB((p) => ({
                    ...p,
                    bg_gradient_from: p.bg_gradient_from || p.bg_color || DEFAULTS.bg_color,
                    bg_gradient_to: p.bg_gradient_to || DEFAULTS.gradient_to,
                    bg_gradient_angle: p.bg_gradient_angle ?? 180,
                  }));
                } else {
                  setB((p) => ({
                    ...p, bg_gradient_from: null, bg_gradient_mid: null,
                    bg_gradient_to: null, bg_gradient_angle: null,
                  }));
                }
                colorsTouched.current = true;
              }}
            />
            <input
              type="color"
              disabled={!gradientOn}
              title="Gradient start"
              value={b.bg_gradient_from || b.bg_color || DEFAULTS.bg_color}
              onChange={(e) => set('bg_gradient_from', e.target.value)}
              style={{ width: 38, height: 32, border: 'none', background: 'none', cursor: gradientOn ? 'pointer' : 'not-allowed', opacity: gradientOn ? 1 : 0.35 }}
            />
            <select
              disabled={!gradientOn}
              value={b.bg_gradient_mid ? '3' : '2'}
              title="Number of gradient colours"
              onChange={(e) => set('bg_gradient_mid',
                e.target.value === '3'
                  ? (b.bg_gradient_mid || DEFAULTS.gradient_mid)
                  : null)}
              style={{ ...input, width: 'auto', padding: '0.3rem 0.35rem', opacity: gradientOn ? 1 : 0.35 }}
            >
              <option value="2">2 colours</option>
              <option value="3">3 colours</option>
            </select>
            {b.bg_gradient_mid && (
              <input
                type="color"
                disabled={!gradientOn}
                title="Gradient middle"
                value={b.bg_gradient_mid}
                onChange={(e) => set('bg_gradient_mid', e.target.value)}
                style={{ width: 38, height: 32, border: 'none', background: 'none', cursor: gradientOn ? 'pointer' : 'not-allowed', opacity: gradientOn ? 1 : 0.35 }}
              />
            )}
            <input
              type="color"
              disabled={!gradientOn}
              title="Gradient end"
              value={b.bg_gradient_to || DEFAULTS.gradient_to}
              onChange={(e) => set('bg_gradient_to', e.target.value)}
              style={{ width: 38, height: 32, border: 'none', background: 'none', cursor: gradientOn ? 'pointer' : 'not-allowed', opacity: gradientOn ? 1 : 0.35 }}
            />
            <select
              disabled={!gradientOn}
              value={String(b.bg_gradient_angle ?? 180)}
              onChange={(e) => set('bg_gradient_angle', Number(e.target.value))}
              title="Gradient direction"
              style={{ ...input, width: 'auto', padding: '0.3rem 0.35rem', opacity: gradientOn ? 1 : 0.35 }}
            >
              <option value="180">↓ top to bottom</option>
              <option value="0">↑ bottom to top</option>
              <option value="90">→ left to right</option>
              <option value="270">← right to left</option>
              <option value="135">↘ diagonal</option>
              <option value="45">↗ diagonal</option>
            </select>
          </div>
        ))}
        {field('Text', <input type="color" value={b.text_color || DEFAULTS.text_color} onChange={(e) => set('text_color', e.target.value)} style={{ width: 44, height: 32, border: 'none', background: 'none', cursor: 'pointer' }} />)}
        {field('Button', <input type="color" value={b.button_color || DEFAULTS.button_color} onChange={(e) => set('button_color', e.target.value)} style={{ width: 44, height: 32, border: 'none', background: 'none', cursor: 'pointer' }} />)}
        {field('Button hover', (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={b.button_hover_color != null}
              title="Change the button color on hover"
              onChange={(e) => set('button_hover_color', e.target.checked ? (b.button_hover_color || DEFAULTS.button_hover_color) : null)}
            />
            <input
              type="color"
              disabled={b.button_hover_color == null}
              value={b.button_hover_color || DEFAULTS.button_hover_color}
              onChange={(e) => set('button_hover_color', e.target.value)}
              style={{
                width: 44, height: 32, border: 'none', background: 'none',
                cursor: b.button_hover_color == null ? 'not-allowed' : 'pointer',
                opacity: b.button_hover_color == null ? 0.35 : 1,
              }}
            />
          </div>
        ))}
        {field('Button text', (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={b.button_text_color != null}
              title="Set the button label color (default #111)"
              onChange={(e) => set('button_text_color', e.target.checked ? (b.button_text_color || DEFAULTS.button_text_color) : null)}
            />
            <input
              type="color"
              disabled={b.button_text_color == null}
              value={b.button_text_color || DEFAULTS.button_text_color}
              onChange={(e) => set('button_text_color', e.target.value)}
              style={{
                width: 44, height: 32, border: 'none', background: 'none',
                cursor: b.button_text_color == null ? 'not-allowed' : 'pointer',
                opacity: b.button_text_color == null ? 0.35 : 1,
              }}
            />
          </div>
        ))}
        {field('Button text hover', (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={b.button_text_hover_color != null}
              title="Change the button label color on hover"
              onChange={(e) => set('button_text_hover_color', e.target.checked ? (b.button_text_hover_color || b.button_text_color || DEFAULTS.button_text_color) : null)}
            />
            <input
              type="color"
              disabled={b.button_text_hover_color == null}
              value={b.button_text_hover_color || b.button_text_color || DEFAULTS.button_text_color}
              onChange={(e) => set('button_text_hover_color', e.target.value)}
              style={{
                width: 44, height: 32, border: 'none', background: 'none',
                cursor: b.button_text_hover_color == null ? 'not-allowed' : 'pointer',
                opacity: b.button_text_hover_color == null ? 0.35 : 1,
              }}
            />
          </div>
        ))}
        <label style={{ fontSize: '0.8rem', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <input type="checkbox" checked={b.dismissible} onChange={(e) => set('dismissible', e.target.checked)} /> Dismissible
        </label>
        <label style={{ fontSize: '0.8rem', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <input type="checkbox" checked={b.button_new_tab} onChange={(e) => set('button_new_tab', e.target.checked)} /> Open button in new window
        </label>
        <button
          type="button"
          onClick={resetColors}
          disabled={colorsMatchTarget}
          title={colorsMatchTarget
            ? `Colors already match ${preset ? `the ${preset.brand_name} brand` : 'the defaults'}`
            : `Restore ${preset ? `the ${preset.brand_name} brand colors` : 'the default banner colors'}`}
          style={{
            fontSize: '0.72rem', fontWeight: 600, padding: '0.35rem 0.6rem', borderRadius: 4,
            border: `1px solid ${theme.cardBorder}`, background: 'transparent',
            color: colorsMatchTarget ? theme.textMuted : theme.textSecondary,
            cursor: colorsMatchTarget ? 'default' : 'pointer',
            opacity: colorsMatchTarget ? 0.5 : 1, fontFamily: 'inherit', marginLeft: 'auto',
          }}
        >
          {preset ? `Reset to ${preset.brand_name} colors` : 'Reset colors to defaults'}
        </button>
      </div>

      <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '-0.4rem 0 0' }}>
        {preset
          ? `Brand colors available from ${preset.brand_name}${isNew ? ' — applied automatically' : ''}.`
          : b.target_type === 'all'
            ? 'Targeting all sites, so no single brand applies — using the global defaults.'
            : 'No brand colors for this target (no community template, or the group spans several brands).'}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field(`Start (${browserTz()})`, <input type="datetime-local" style={input} value={isoToLocalInput(b.start_at)} onChange={(e) => set('start_at', localInputToIso(e.target.value))} />)}
        {field(`End (${browserTz()})`, <input type="datetime-local" style={input} value={isoToLocalInput(b.end_at)} onChange={(e) => set('end_at', localInputToIso(e.target.value))} />)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.82rem', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <input type="checkbox" checked={b.is_enabled} onChange={(e) => set('is_enabled', e.target.checked)} /> Enabled
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={onCancel} style={{
            fontSize: '0.8rem', fontWeight: 600, padding: '0.45rem 1rem', borderRadius: 4, cursor: 'pointer',
            fontFamily: 'inherit', background: 'transparent', color: theme.textSecondary, border: `1px solid ${theme.cardBorder}`,
          }}>Cancel</button>
          <button type="button" disabled={!valid || saving} onClick={() => onSave(b)} style={{
            fontSize: '0.8rem', fontWeight: 600, padding: '0.45rem 1rem', borderRadius: 4,
            cursor: (!valid || saving) ? 'default' : 'pointer', fontFamily: 'inherit',
            background: theme.accent, color: '#fff', border: `1px solid ${theme.accent}`, opacity: (!valid || saving) ? 0.6 : 1,
          }}>{saving ? 'Saving…' : 'Save banner'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main view ───────────────────────────────────────────────────────────────

export const BannerManagerView: React.FC<BannerManagerProps> = ({ theme }) => {
  const [client, setClient] = useState<BackendClientId>(BANNER_CLIENTS[0].key);
  const [data, setData] = useState<BannerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: number | null; input: BannerInput } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchBanners(client)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const targets = data?.targets ?? { groups: [], sites: [] };
  const presets = data?.brand_presets ?? {};

  const startNew = () => setEditing({ id: null, input: emptyBanner() });
  const startNewForSite = (siteId: number) =>
    setEditing({ id: null, input: { ...emptyBanner(), target_type: 'site', target_site_id: siteId } });
  const startEdit = (b: Banner) => setEditing({
    id: b.id,
    input: {
      name: b.name, target_type: b.target_type, target_group: b.target_group ?? null,
      target_site_id: b.target_site_id ?? null, headline: b.headline ?? '', message: b.message ?? '',
      button_label: b.button_label ?? '', button_url: b.button_url ?? '',
      bg_color: b.bg_color ?? DEFAULTS.bg_color, text_color: b.text_color ?? DEFAULTS.text_color,
      button_color: b.button_color ?? DEFAULTS.button_color,
      button_hover_color: b.button_hover_color ?? null,
      button_text_color: b.button_text_color ?? null,
      button_text_hover_color: b.button_text_hover_color ?? null,
      bg_gradient_from: b.bg_gradient_from ?? null,
      bg_gradient_mid: b.bg_gradient_mid ?? null,
      bg_gradient_to: b.bg_gradient_to ?? null,
      bg_gradient_angle: b.bg_gradient_angle ?? null,
      button_new_tab: b.button_new_tab, dismissible: b.dismissible,
      start_at: b.start_at ?? null, end_at: b.end_at ?? null, is_enabled: b.is_enabled,
    },
  });

  const save = async (input: BannerInput) => {
    if (!editing) return;
    setSaving(true); setError(null);
    try {
      if (editing.id == null) await createBanner(client, input);
      else await updateBanner(client, editing.id, input);
      setEditing(null);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const remove = async (b: Banner) => {
    if (!window.confirm(`Delete banner "${b.name}"?`)) return;
    setError(null);
    try { await deleteBanner(client, b.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const banners = data?.banners ?? [];
  const activeCount = useMemo(() => (data?.banners ?? []).filter((x) => x.status === 'active').length, [data]);

  // Group banners for display: per-site sections first (the primary unit —
  // ~3 per site, rotating by schedule), then per community-group, then all-sites.
  const grouped = useMemo(() => {
    const list = data?.banners ?? [];
    const bySite = new Map<number, { name: string; banners: Banner[] }>();
    const byGroup = new Map<string, Banner[]>();
    const allSites: Banner[] = [];
    for (const b of list) {
      if (b.target_type === 'site' && b.target_site_id != null) {
        const key = b.target_site_id;
        if (!bySite.has(key)) bySite.set(key, { name: b.target_site_name || `Site ${key}`, banners: [] });
        bySite.get(key)!.banners.push(b);
      } else if (b.target_type === 'group') {
        const key = b.target_group || '—';
        if (!byGroup.has(key)) byGroup.set(key, []);
        byGroup.get(key)!.push(b);
      } else {
        allSites.push(b);
      }
    }
    const sites = Array.from(bySite.entries())
      .map(([id, v]) => ({ id, name: v.name, banners: v.banners }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const groups = Array.from(byGroup.entries())
      .map(([name, bs]) => ({ name, banners: bs }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { sites, groups, allSites };
  }, [data]);

  const sectionHeader = (text: string, count?: number, action?: React.ReactNode) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
      margin: '0 0 0.5rem', paddingBottom: '0.35rem', borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>
      <span style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: theme.sectionTitle }}>
        {text}{count != null && <span style={{ color: theme.textMuted }}> · {count}</span>}
      </span>
      {action}
    </div>
  );

  const renderCard = (b: Banner) => (
    <div key={b.id} style={{
      background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow,
      borderRadius: 4, padding: '0.9rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <StatusBadge status={b.status} />
          <span style={{ fontSize: '0.92rem', fontWeight: 700, color: theme.textPrimary }}>{b.name}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="button" onClick={() => startEdit(b)} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.3rem 0.7rem', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: theme.textSecondary, border: `1px solid ${theme.cardBorder}` }}>Edit</button>
          <button type="button" onClick={() => remove(b)} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.3rem 0.7rem', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: theme.negative, border: `1px solid ${theme.cardBorder}` }}>Delete</button>
        </div>
      </div>
      <BannerPreview b={b} />
      <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{fmtWindow(b)}</div>
    </div>
  );

  const addHereBtn = (siteId: number) => (
    <button type="button" onClick={() => startNewForSite(siteId)} style={{
      fontSize: '0.7rem', fontWeight: 600, padding: '0.25rem 0.6rem', borderRadius: 4, cursor: 'pointer',
      fontFamily: 'inherit', background: 'transparent', color: theme.accent, border: `1px solid ${theme.cardBorder}`,
    }}>+ Add banner</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>Banner Manager</h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Schedule banners for community sites.
            {data && <> · {activeCount} active · {banners.length} total</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={client} onChange={(e) => { setClient(e.target.value as BackendClientId); setEditing(null); }}
            style={{ fontSize: '0.82rem', padding: '0.35rem 0.55rem', borderRadius: 4, border: `1px solid ${theme.cardBorder}`, background: theme.cardBg, color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer' }}>
            {BANNER_CLIENTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {!editing && (
            <button type="button" onClick={startNew} style={{
              fontSize: '0.8rem', fontWeight: 600, padding: '0.4rem 0.9rem', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'inherit', background: theme.accent, color: '#fff', border: `1px solid ${theme.accent}`,
            }}>+ New banner</button>
          )}
        </div>
      </div>

      {/* Install help — how to render these banners on a site */}
      <details style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: 4, padding: '0.6rem 0.9rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: theme.textSecondary }}>
          How to add a banner to a site (WordPress plugin or JavaScript embed)
        </summary>
        <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: theme.textSecondary, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div>
            <strong style={{ color: theme.textPrimary }}>JavaScript embed</strong> <span style={{ color: theme.positive, fontWeight: 600 }}>(recommended)</span> — no plugin, works on any site, and is more resilient to page caching. Place where the banner should appear (replace <code>YOUR_DOMAIN</code>):
            <pre style={{ background: theme.pageBg, border: `1px solid ${theme.cardBorder}`, borderRadius: 4, padding: '0.5rem 0.6rem', overflowX: 'auto', margin: '0.3rem 0 0', color: theme.textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{`<div id="lupa-banner"></div>\n<script src="${window.location.origin}/api/banners/embed.js?site=YOUR_DOMAIN" async></script>`}</pre>
          </div>
          <div>
            <strong style={{ color: theme.textPrimary }}>WordPress plugin</strong> <span style={{ color: theme.textMuted }}>(alternative)</span> — install the Lupa Banner plugin, then add this shortcode (e.g. in a Divi Code module):
            <pre style={{ background: theme.pageBg, border: `1px solid ${theme.cardBorder}`, borderRadius: 4, padding: '0.5rem 0.6rem', overflowX: 'auto', margin: '0.3rem 0 0', color: theme.textPrimary }}>[lupa_banner]</pre>
          </div>
          <div style={{ color: theme.textMuted }}>Both render the currently-scheduled banner (or nothing, so the section collapses).</div>
        </div>
      </details>

      {error && (
        <div style={{ background: theme.cardBg, border: `1px solid ${theme.negative}`, borderRadius: 4, padding: '0.85rem 1.1rem', color: theme.negative, fontSize: '0.85rem' }}>{error}</div>
      )}

      {editing && (
        <Editor theme={theme} initial={editing.input} targets={targets} presets={presets}
          isNew={editing.id == null} saving={saving}
          onSave={save} onCancel={() => setEditing(null)} />
      )}

      {!editing && !loading && banners.length === 0 && (
        <div style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: 4, padding: '2rem', textAlign: 'center', color: theme.textMuted }}>
          No banners yet. Click “+ New banner” to create one.
        </div>
      )}

      {/* Per-site sections (primary): ~3 banners per site, rotating by schedule. */}
      {!editing && grouped.sites.map((site) => (
        <div key={`site-${site.id}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {sectionHeader(site.name, site.banners.length, addHereBtn(site.id))}
          {site.banners.map(renderCard)}
        </div>
      ))}

      {/* Community-group-targeted banners (apply to every site in the group). */}
      {!editing && grouped.groups.map((g) => (
        <div key={`group-${g.name}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {sectionHeader(`Group: ${g.name}`, g.banners.length)}
          {g.banners.map(renderCard)}
        </div>
      ))}

      {/* All-sites banners. */}
      {!editing && grouped.allSites.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {sectionHeader('All sites', grouped.allSites.length)}
          {grouped.allSites.map(renderCard)}
        </div>
      )}
    </div>
  );
};

export default BannerManagerView;
